import type { RootState } from "../state";

/**
 * 保持字幕与实际播放音频一致：
 * - 以“正在发声的音频ID”为唯一真相源
 * - 当 UI 当前索引对应的项与实际在播项ID不一致时，立即将 currentIndex 对齐
 * - 同步以时间为基准的进度（audioTimeProgress/playedCharCount），避免文本回退
 */
export function reconcileSubtitleWithAudible(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  try {
    if (!roomId || !this.roomStates.has(roomId)) return;
    const state = this.roomStates.get(roomId);
    if (!state || !Array.isArray(state.queue) || state.queue.length === 0) return;

    // 选择真实播放源：优先 WebAudio（苹果隐藏态）；否则 Howler
    let status: any = null;
    try {
      const wa: any = (state as any).__appleWA__;
      if (wa && wa.player && typeof wa.player.getStatus === "function") {
        const s = wa.player.getStatus();
        if (s && s.isPlaying && s.duration > 0) status = s;
      }
    } catch {}
    if (!status) {
      try {
        const hp: any = (state as any).howlerPlayer;
        if (hp && typeof hp.getStatus === "function") {
          const s = hp.getStatus();
          if (s && s.isPlaying && s.duration > 0) status = s;
        }
      } catch {}
    }
    if (!status || !status.currentItem || !status.currentItem.id) return;

    const audibleId: string = status.currentItem.id;
    const uiIndex: number = state.currentIndex ?? 0;
    const uiItem = state.queue[uiIndex];

    if (uiItem && uiItem.id === audibleId) {
      // 同步时间型进度，避免 UI 滞后（防倒退）
      try {
        const progress = Math.max(0, Math.min(1, (status.currentTime || 0) / (status.duration || 1)));
        const cur = state.queue[uiIndex];
        if (cur && typeof cur.text === "string") {
          (cur as any).audioTimeProgress = progress;
          const total = cur.text.length || 0;
          const chars = Math.floor(progress * total);
          if (!cur.playedCharCount || chars > cur.playedCharCount) {
            cur.playedCharCount = chars;
          }
          // 触发响应式
          const q = [...state.queue];
          q[uiIndex] = { ...cur } as any;
          state.queue = q as any;
        }
      } catch {}
      return;
    }

    // 若不一致，定位 audibleId 在队列中的位置
    let k = -1;
    // 先在当前索引附近小窗口搜索，减少全表扫描
    const start = Math.max(0, uiIndex - 5);
    const end = Math.min(state.queue.length - 1, uiIndex + 8);
    for (let i = start; i <= end; i++) {
      if (state.queue[i] && state.queue[i].id === audibleId) { k = i; break; }
    }
    if (k === -1) {
      // 全量兜底搜索
      k = state.queue.findIndex((it: any) => it && it.id === audibleId);
    }
    if (k < 0) return; // 队列中未找到（可能是预热/保活），不强行对齐

    // 对齐 currentIndex
    state.currentIndex = k;

    // 用真实时间进度回写 UI（防倒退）
    try {
      const progress = Math.max(0, Math.min(1, (status.currentTime || 0) / (status.duration || 1)));
      const cur = state.queue[k];
      if (cur && typeof cur.text === "string") {
        (cur as any).audioTimeProgress = progress;
        const total = cur.text.length || 0;
        const chars = Math.floor(progress * total);
        if (!cur.playedCharCount || chars > cur.playedCharCount) {
          cur.playedCharCount = chars;
        }
        const q = [...state.queue];
        q[k] = { ...cur } as any;
        state.queue = q as any;
      }
    } catch {}
  } catch {}
}

